import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  ReactNode,
} from "react";
import {
  getMessagesApp,
  setMessageStatus,
  setAllActionableMessageStatus,
} from "../services/notificationService";
import { useUserContext } from "./UserContext";
import {
  resolveNotificationTarget,
  transformNotification,
} from "../utils/transformNotification";
import {
  invitationNotificationShouldShowActions,
  offerNotificationShouldShowActions,
  badgeRequestNotificationShouldShowActions,
  questionNotificationShouldShowActions,
  isPendingAcceptanceStatus,
  isActiveIssuerBadgeRequest,
  commitmentNeedsAnswer,
} from "../utils/notificationActionSync";
import { normalizeApiId, resolveCommitmentApiId } from "../utils/commitmentIds";
import { useCommitmentsContext } from "../hooks/useCommitmentsContext";

// Default: only `status === "unread"` rows. Stale rows often stay `read` + `actionable: "yes"`
// until the backend clears them — avoid loading those in production.
//
// Dev-only: VITE_NOTIFICATIONS_INCLUDE_READ_FOR_ACTIONABLE=true includes read rows that are
// still flagged actionable (e.g. WhatsApp marked read early). That can surface duplicates;
// production should honor backend read state so cleared notifications do not reappear.
const SHOW_UNREAD_ONLY =
  import.meta.env.VITE_NOTIFICATIONS_UNREAD_ONLY !== "false";

const INCLUDE_READ_FOR_ACTIONABLE =
  import.meta.env.VITE_NOTIFICATIONS_INCLUDE_READ_FOR_ACTIONABLE === "true";

const SUPPRESSED_TARGETS_STORAGE_KEY =
  "reliably_suppressed_notification_targets_v1";

const readSuppressedTargetKeys = (): Set<string> => {
  if (typeof sessionStorage === "undefined") return new Set();
  try {
    const raw = sessionStorage.getItem(SUPPRESSED_TARGETS_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed.map(String) : []);
  } catch {
    return new Set();
  }
};

/** Same key as actionable dedupe: one completed action hides all rows for that target. */
const getActionSuppressionKeyFromRow = (notification: any): string | null => {
  const target = resolveNotificationTarget(notification);
  if (target.actionable) {
    return target.actionKey || `${target.targetType}:${target.targetId}`;
  }
  return null;
};

interface NotificationsContextType {
  notifications: any[];
  nonActionNotifications: any[];
  actionableNotifications: any[];
  loading: boolean;
  error: string | null;
  fetchNotifications: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  /** After a successful Issue/Deny/Accept/Reject/Clarify, hides this target until new data (session-scoped). */
  suppressCompletedActionTarget: (rawNotification: any) => void;
}

const NotificationsContext = createContext<
  NotificationsContextType | undefined
>(undefined);

const getNotificationTimestamp = (notification: any) => {
  const timestamp =
    notification?.createdAt || notification?.updatedAt || notification?.date;
  if (!timestamp) return 0;
  const parsed = Date.parse(String(timestamp));
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getLogicalMessageKey = (notification: any) => {
  const target = resolveNotificationTarget(notification);
  if (target.actionable) {
    return target.actionKey || `${target.targetType}:${target.targetId}`;
  }

  const rowId = notification?._id || notification?.id;
  if (rowId) {
    return `msg:${String(rowId)}`;
  }
  const message = String(notification?.message || "");
  const createdAt = String(notification?.createdAt || notification?.updatedAt || "");
  return `msg:${message}|${createdAt}`;
};

export const NotificationsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { getUserId, isAuthenticated } = useUserContext();
  const {
    receivedInvitations,
    receivedCommitments,
    myCommitments,
    loading: commitmentsLoading,
    lastFetched,
    fetchAllCommitments,
  } = useCommitmentsContext();
  const [notifications, setNotifications] = useState<any[]>([]);
  const [suppressedTargetKeys, setSuppressedTargetKeys] = useState<
    Set<string>
  >(() => readSuppressedTargetKeys());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const persistSuppressedKeys = useCallback((keys: string[]) => {
    if (keys.length === 0) return;
    setSuppressedTargetKeys((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => next.add(k));
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem(
          SUPPRESSED_TARGETS_STORAGE_KEY,
          JSON.stringify([...next])
        );
      }
      return next;
    });
  }, []);

  const removeSuppressedKeys = useCallback((keys: string[]) => {
    if (keys.length === 0) return;
    setSuppressedTargetKeys((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => next.delete(k));
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem(
          SUPPRESSED_TARGETS_STORAGE_KEY,
          JSON.stringify([...next])
        );
      }
      return next;
    });
  }, []);

  const suppressCompletedActionTarget = useCallback(
    (rawNotification: any) => {
      const key = getActionSuppressionKeyFromRow(rawNotification);
      if (key) {
        persistSuppressedKeys([key]);
      }
    },
    [persistSuppressedKeys]
  );

  useEffect(() => {
    if (!isAuthenticated || !getUserId()) {
      return;
    }
    void fetchAllCommitments();
  }, [isAuthenticated, getUserId, fetchAllCommitments]);

  const fetchNotifications = useCallback(async () => {
    const userId = getUserId();
    if (!userId || !isAuthenticated) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await getMessagesApp();
      const data =
        response.data?.results?.data ||
        response.data?.data ||
        response.data ||
        [];
      const allNotifications = Array.isArray(data) ? data : [];
      const visibleNotifications = SHOW_UNREAD_ONLY
        ? allNotifications.filter(
            (n: any) =>
              n.status === "unread" ||
              (INCLUDE_READ_FOR_ACTIONABLE && n.actionable === "yes")
          )
        : allNotifications;
      setNotifications(visibleNotifications);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch notifications",
      );
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [getUserId, isAuthenticated]);

  /** All received commitments we know about (any status) — used to detect stale notifications. */
  const knownReceivedCommitmentIdSet = useMemo(() => {
    const ids = new Set<string>();
    for (const c of receivedCommitments as any[]) {
      const apiId = resolveCommitmentApiId(c) || normalizeApiId(c?._id || c?.id);
      if (apiId) ids.add(apiId);
    }
    return ids;
  }, [receivedCommitments]);

  /** Offers awaiting issuer Accept/Reject — matches getBadgeRequestsForActions. */
  const pendingOfferCommitmentIdSet = useMemo(() => {
    return new Set(
      receivedCommitments
        .filter((c: any) => isPendingAcceptanceStatus(c.status))
        .map(
          (c: any) =>
            resolveCommitmentApiId(c) || normalizeApiId(c._id || c.id)
        )
        .filter(Boolean)
    );
  }, [receivedCommitments]);

  /** Badge requests awaiting Issue / Deny / Clarify (not after Clarify / re-request). */
  const pendingBadgeIssuerCommitmentIdSet = useMemo(() => {
    return new Set(
      receivedCommitments
        .filter((c: any) => isActiveIssuerBadgeRequest(c))
        .map(
          (c: any) =>
            resolveCommitmentApiId(c) || normalizeApiId(c._id || c.id)
        )
        .filter(Boolean)
    );
  }, [receivedCommitments]);

  /** Questions awaiting committer answer (status/p2p question state). */
  const pendingQuestionCommitmentIdSet = useMemo(() => {
    return new Set(
      myCommitments
        .filter((c: any) => commitmentNeedsAnswer(c))
        .map(
          (c: any) =>
            resolveCommitmentApiId(c) || normalizeApiId(c._id || c.id)
        )
        .filter(Boolean)
    );
  }, [myCommitments]);

  const canSyncActionableState =
    lastFetched.invitations != null &&
    lastFetched.myCommitments != null &&
    lastFetched.receivedCommitments != null &&
    !commitmentsLoading.invitations &&
    !commitmentsLoading.myCommitments &&
    !commitmentsLoading.receivedCommitments;

  const autoMarkedStaleMessageIdsRef = useRef<Set<string>>(new Set());

  /**
   * Session suppression hides actionable rows after you complete an in-app action, keyed by
   * target (e.g. commitment id). The same commitment can enter a new badge-request / offer cycle
   * later with a new unread message — if we kept suppression, that message would never show.
   * Clear suppression for any target that currently has an unread actionable row from the API.
   */
  useEffect(() => {
    const keysToClear: string[] = [];
    for (const row of notifications) {
      if (row?.status !== "unread" || row?.actionable !== "yes") {
        continue;
      }
      const sk = getActionSuppressionKeyFromRow(row);
      if (sk && suppressedTargetKeys.has(sk)) {
        keysToClear.push(sk);
      }
    }
    if (keysToClear.length === 0) {
      return;
    }
    removeSuppressedKeys(keysToClear);
  }, [notifications, suppressedTargetKeys, removeSuppressedKeys]);

  const { nonActionNotifications, actionableNotifications } = useMemo(() => {
    const nonAction: any[] = [];
    const byKey = new Map<string, any>();

    notifications.forEach((row) => {
      if (row?.actionable === "yes") {
        const sk = getActionSuppressionKeyFromRow(row);
        if (sk && suppressedTargetKeys.has(sk)) {
          return;
        }
      }
      const card = transformNotification(row);
      let cardForUi = card;
      if (
        canSyncActionableState &&
        card.actionType === "invitation" &&
        Array.isArray(card.actions) &&
        card.actions.length > 0 &&
        !invitationNotificationShouldShowActions(
          row,
          receivedInvitations,
          myCommitments
        )
      ) {
        cardForUi = { ...card, actions: [] };
      } else if (
        canSyncActionableState &&
        card.actionType === "offer" &&
        Array.isArray(card.actions) &&
        card.actions.length > 0 &&
        !offerNotificationShouldShowActions(
          row,
          pendingOfferCommitmentIdSet,
          knownReceivedCommitmentIdSet
        )
      ) {
        cardForUi = { ...card, actions: [] };
      } else if (
        canSyncActionableState &&
        card.actionType === "badge-request" &&
        Array.isArray(card.actions) &&
        card.actions.length > 0 &&
        !badgeRequestNotificationShouldShowActions(
          row,
          pendingBadgeIssuerCommitmentIdSet,
          knownReceivedCommitmentIdSet
        )
      ) {
        cardForUi = { ...card, actions: [] };
      } else if (
        canSyncActionableState &&
        card.actionType === "question" &&
        Array.isArray(card.actions) &&
        card.actions.length > 0 &&
        !questionNotificationShouldShowActions(
          row,
          pendingQuestionCommitmentIdSet
        )
      ) {
        cardForUi = { ...card, actions: [] };
      }

      const hasActions =
        Array.isArray(cardForUi.actions) && cardForUi.actions.length > 0;
      if (!hasActions) {
        nonAction.push(cardForUi);
        return;
      }

      const target = resolveNotificationTarget(row);
      if (!target.actionable) {
        nonAction.push(cardForUi);
        return;
      }

      const dedupeKey =
        target.actionKey || `${target.targetType}:${target.targetId}`;

      const existing = byKey.get(dedupeKey);
      if (
        existing &&
        getNotificationTimestamp(existing.rawNotification) >
          getNotificationTimestamp(row)
      ) {
        return;
      }
      byKey.set(dedupeKey, cardForUi);
    });

    return {
      nonActionNotifications: nonAction,
      actionableNotifications: [...byKey.values()],
    };
  }, [
    notifications,
    suppressedTargetKeys,
    canSyncActionableState,
    receivedInvitations,
    myCommitments,
    pendingOfferCommitmentIdSet,
    pendingBadgeIssuerCommitmentIdSet,
    pendingQuestionCommitmentIdSet,
    knownReceivedCommitmentIdSet,
  ]);

  useEffect(() => {
    if (!canSyncActionableState) {
      return;
    }
    const idsToMark: string[] = [];
    for (const row of notifications) {
      // Unread and read+actionable both need cleanup: WhatsApp often marks messages
      // read before the web app acts, and backend may leave actionable=yes after issue.
      if (row.actionable !== "yes") {
        continue;
      }
      const card = transformNotification(row);
      if (!card.actions?.length) {
        continue;
      }
      let stale = false;
      if (card.actionType === "invitation") {
        stale = !invitationNotificationShouldShowActions(
          row,
          receivedInvitations,
          myCommitments
        );
      } else if (card.actionType === "offer") {
        stale = !offerNotificationShouldShowActions(
          row,
          pendingOfferCommitmentIdSet,
          knownReceivedCommitmentIdSet
        );
      } else if (card.actionType === "badge-request") {
        stale = !badgeRequestNotificationShouldShowActions(
          row,
          pendingBadgeIssuerCommitmentIdSet,
          knownReceivedCommitmentIdSet
        );
      } else if (card.actionType === "question") {
        stale = !questionNotificationShouldShowActions(
          row,
          pendingQuestionCommitmentIdSet
        );
      }
      if (!stale) {
        continue;
      }
      const key = getLogicalMessageKey(row);
      const ids = notifications
        .filter((n) => getLogicalMessageKey(n) === key)
        .map((n) => String(n._id || n.id || ""))
        .filter(Boolean);
      idsToMark.push(...ids);
    }
    const unique = [...new Set(idsToMark)].filter(
      (id) => !autoMarkedStaleMessageIdsRef.current.has(id)
    );
    if (unique.length === 0) {
      return;
    }
    void (async () => {
      for (const id of unique) {
        autoMarkedStaleMessageIdsRef.current.add(id);
      }
      try {
        await Promise.all(
          unique.map((messageId) =>
            setMessageStatus({ messageId, status: "read" })
          )
        );
        // Drop from local list so Action Required / Dashboard no longer show Issue.
        setNotifications((prev) =>
          prev.filter((n) => !unique.includes(String(n._id || n.id)))
        );
      } catch {
        unique.forEach((id) => autoMarkedStaleMessageIdsRef.current.delete(id));
      }
    })();
  }, [
    notifications,
    canSyncActionableState,
    receivedInvitations,
    myCommitments,
    pendingOfferCommitmentIdSet,
    pendingBadgeIssuerCommitmentIdSet,
    pendingQuestionCommitmentIdSet,
    knownReceivedCommitmentIdSet,
  ]);

  const allDisplayNotifications = useMemo(() => {
    const combined = [...nonActionNotifications, ...actionableNotifications];
    return combined.sort(
      (a, b) =>
        getNotificationTimestamp((b as any).rawNotification || b) -
        getNotificationTimestamp((a as any).rawNotification || a)
    );
  }, [nonActionNotifications, actionableNotifications]);

  const markAsRead = useCallback(async (id: string) => {
    const targetNotification = notifications.find(
      (n) => String(n._id || n.id) === String(id)
    );
    const key = targetNotification ? getLogicalMessageKey(targetNotification) : "";
    const idsToMarkRead = key
      ? notifications
          .filter((n) => getLogicalMessageKey(n) === key)
          .map((n) => String(n._id || n.id || ""))
          .filter(Boolean)
      : [String(id)];
    const uniqueIds = [...new Set(idsToMarkRead)];

    try {
      await Promise.all(
        uniqueIds.map((messageId) =>
          setMessageStatus({ messageId, status: "read" })
        )
      );
      setNotifications((prev) =>
        prev.filter((n) => !uniqueIds.includes(String(n._id || n.id)))
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to mark as read",
      );
    }
  }, [notifications]);

  const markAllAsRead = useCallback(async () => {
    const userId = getUserId();
    if (!userId) return;

    try {
      await setAllActionableMessageStatus({ status: "read" });
      setNotifications([]);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to mark all as read",
      );
    }
  }, [getUserId]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const value: NotificationsContextType = {
    notifications: allDisplayNotifications,
    nonActionNotifications,
    actionableNotifications,
    loading,
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    suppressCompletedActionTarget,
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotificationsContext = () => {
  const context = useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error(
      "useNotificationsContext must be used within a NotificationsProvider",
    );
  }
  return context;
};
